import React, { useState, useRef, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Sparkles, Send, Loader2, User, RotateCcw } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function CapexAssistantPage() {
  const { token, user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const endRef = useRef(null);
  const authHeaders = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/capex-assistant/suggestions`, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : { suggestions: [] })
      .then(d => setSuggestions(d.suggestions || []))
      .catch(() => {});
  }, [token]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, loading]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || loading) return;
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: msg }]);
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/capex-assistant/chat`, {
        method: 'POST', headers: authHeaders,
        body: JSON.stringify({ message: msg, session_id: sessionId }),
      });
      if (!res.ok) throw new Error();
      const d = await res.json();
      setSessionId(d.session_id);
      setMessages(prev => [...prev, { role: 'assistant', content: d.answer }]);
    } catch (e) {
      toast.error("L'assistant est momentanément indisponible");
      setMessages(prev => [...prev, { role: 'assistant', content: "Désolé, une erreur est survenue. Réessayez dans un instant." }]);
    } finally { setLoading(false); }
  };

  const reset = () => { setMessages([]); setSessionId(null); };

  return (
    <div className="max-w-3xl mx-auto px-4 py-6 flex flex-col h-[calc(100vh-2rem)]" data-testid="capex-assistant-page">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-orange-500 to-amber-400 flex items-center justify-center shadow-lg shadow-orange-500/20">
            <Sparkles className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-slate-800 dark:text-slate-100">Assistant CAPEX</h1>
            <p className="text-sm text-slate-500">Posez vos questions en langage naturel sur vos données.</p>
          </div>
        </div>
        {messages.length > 0 && (
          <Button variant="outline" size="sm" onClick={reset} data-testid="assistant-reset-btn">
            <RotateCcw className="w-4 h-4" /> <span className="ml-1.5">Nouvelle conversation</span>
          </Button>
        )}
      </div>

      <Card className="flex-1 flex flex-col overflow-hidden border-slate-200 dark:border-slate-800">
        <CardContent className="flex-1 overflow-y-auto p-4 space-y-4" data-testid="assistant-messages">
          {messages.length === 0 && (
            <div className="h-full flex flex-col items-center justify-center text-center gap-5 py-10">
              <div className="w-16 h-16 rounded-2xl bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center">
                <Sparkles className="w-8 h-8 text-orange-500" />
              </div>
              <div>
                <p className="text-slate-600 dark:text-slate-300 font-medium">Bonjour {user?.name?.split(' ')[0] || ''} 👋</p>
                <p className="text-sm text-slate-400 mt-1">Essayez une de ces questions :</p>
              </div>
              <div className="flex flex-wrap justify-center gap-2 max-w-xl">
                {suggestions.map((s, i) => (
                  <button key={i} onClick={() => send(s)}
                    className="text-sm px-3 py-2 rounded-full border border-slate-200 dark:border-slate-700 hover:border-orange-400 hover:bg-orange-50 dark:hover:bg-orange-900/20 text-slate-600 dark:text-slate-300 transition-colors"
                    data-testid={`assistant-suggestion-${i}`}>
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((m, i) => (
            <div key={i} className={`flex gap-3 ${m.role === 'user' ? 'flex-row-reverse' : ''}`} data-testid={`assistant-msg-${m.role}-${i}`}>
              <div className={`w-8 h-8 rounded-full shrink-0 flex items-center justify-center ${m.role === 'user' ? 'bg-slate-200 dark:bg-slate-700' : 'bg-gradient-to-br from-orange-500 to-amber-400'}`}>
                {m.role === 'user' ? <User className="w-4 h-4 text-slate-600 dark:text-slate-200" /> : <Sparkles className="w-4 h-4 text-white" />}
              </div>
              <div className={`rounded-2xl px-4 py-2.5 max-w-[80%] text-sm ${m.role === 'user' ? 'bg-orange-500 text-white' : 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200'}`}>
                {m.role === 'assistant'
                  ? <div className="prose prose-sm dark:prose-invert max-w-none prose-p:my-1 prose-ul:my-1 prose-headings:my-1 prose-headings:text-base"><ReactMarkdown>{m.content}</ReactMarkdown></div>
                  : <span className="whitespace-pre-wrap">{m.content}</span>}
              </div>
            </div>
          ))}

          {loading && (
            <div className="flex gap-3" data-testid="assistant-loading">
              <div className="w-8 h-8 rounded-full shrink-0 flex items-center justify-center bg-gradient-to-br from-orange-500 to-amber-400">
                <Sparkles className="w-4 h-4 text-white" />
              </div>
              <div className="rounded-2xl px-4 py-3 bg-slate-100 dark:bg-slate-800">
                <Loader2 className="w-4 h-4 animate-spin text-slate-400" />
              </div>
            </div>
          )}
          <div ref={endRef} />
        </CardContent>

        <div className="border-t border-slate-200 dark:border-slate-800 p-3 flex items-end gap-2">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder="Posez votre question… (ex. CA signé ce trimestre ?)"
            rows={1}
            className="flex-1 resize-none max-h-32 px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm text-slate-700 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-orange-400"
            data-testid="assistant-input"
          />
          <Button onClick={() => send()} disabled={loading || !input.trim()} className="bg-orange-500 hover:bg-orange-600 rounded-xl" data-testid="assistant-send-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </Button>
        </div>
      </Card>
      <p className="text-xs text-slate-400 text-center mt-2">L'assistant consulte des données agrégées en lecture seule. Les chiffres reflètent l'instantané du jour.</p>
    </div>
  );
}
